import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ColumnSelectionItem } from './ColumnSelection.props';
import {
  ALL_GROUPS,
  buildGroupViews,
  countSelected,
  GroupSummary,
  GroupView,
  stickyKey,
  summariseGroups,
  tokenise,
} from './columnSelectionHelpers';

interface UseColumnSelectionFiltersReturn {
  query: string;
  setQuery: (query: string) => void;
  selectedOnly: boolean;
  toggleSelectedOnly: () => void;
  activeGroup: string;
  setActiveGroup: (group: string) => void;
  /** `activeGroup`, or ALL_GROUPS when a search has emptied it. */
  effectiveGroup: string;
  /** Groups with at least one match, for the sidebar. */
  summaries: Array<GroupSummary>;
  /** The right pane's groups, already filtered and order-preserved. */
  views: Array<GroupView>;
  /** True when search or selected-only is narrowing the list. */
  isFiltered: boolean;
  totals: { selected: number; total: number };
  /** Call before flipping a row off so it survives the selected-only filter. */
  retainRow: (groupIndex: number, field: string) => void;
  /** Drop every filter and go back to showing all groups. */
  clearFilters: () => void;
}

/**
 * View state for the column picker: search text, the selected-only toggle and
 * the active sidebar group, plus everything derived from them.
 *
 * Deliberately holds no copy of the selection itself — the picker applies
 * changes live through `onChange`, so `groups` is always the caller's current
 * value and there is no draft to fall out of sync.
 */
export const useColumnSelectionFilters = (
  open: boolean,
  groups: Array<ColumnSelectionItem>
): UseColumnSelectionFiltersReturn => {
  const [query, setQuery] = useState('');
  const [selectedOnly, setSelectedOnly] = useState(false);
  const [activeGroup, setActiveGroup] = useState<string>(ALL_GROUPS);

  // Rows the user has just unticked while "selected only" is on. Without this
  // the row would vanish from under the cursor the instant it was unticked,
  // making a second click land on whatever slid up into its place.
  const stickyRef = useRef<Set<string>>(new Set());

  // Start every visit from a clean view. Keyed on `open` alone: several call
  // sites keep this component mounted with open={false}, and a few rebuild
  // their `groups` array inline on every render, so reacting to `groups` here
  // would reset the user's search mid-interaction.
  useEffect(() => {
    if (!open) return;
    setQuery('');
    setSelectedOnly(false);
    setActiveGroup(ALL_GROUPS);
    stickyRef.current = new Set();
  }, [open]);

  const tokens = useMemo(() => tokenise(query), [query]);

  const summaries = useMemo(
    () => summariseGroups(groups, tokens).filter((summary) => summary.total > 0),
    [groups, tokens]
  );

  // A search can empty the group the user was in. Fall back to "All columns"
  // for display without discarding their choice, so clearing the search
  // returns them to where they were.
  const effectiveGroup = useMemo(
    () =>
      activeGroup === ALL_GROUPS || summaries.some((summary) => summary.title === activeGroup)
        ? activeGroup
        : ALL_GROUPS,
    [activeGroup, summaries]
  );

  const views = useMemo(
    () => buildGroupViews(groups, tokens, effectiveGroup, selectedOnly, stickyRef.current),
    [groups, tokens, effectiveGroup, selectedOnly]
  );

  const totals = useMemo(() => countSelected(groups), [groups]);

  const retainRow = useCallback(
    (groupIndex: number, field: string) => {
      if (selectedOnly) stickyRef.current.add(stickyKey(groupIndex, field));
    },
    [selectedOnly]
  );

  const toggleSelectedOnly = useCallback(() => {
    stickyRef.current = new Set();
    setSelectedOnly((previous) => !previous);
  }, []);

  const handleSetQuery = useCallback((next: string) => {
    stickyRef.current = new Set();
    setQuery(next);
  }, []);

  const clearFilters = useCallback(() => {
    stickyRef.current = new Set();
    setQuery('');
    setSelectedOnly(false);
    setActiveGroup(ALL_GROUPS);
  }, []);

  return {
    query,
    setQuery: handleSetQuery,
    selectedOnly,
    toggleSelectedOnly,
    activeGroup,
    setActiveGroup,
    effectiveGroup,
    summaries,
    views,
    isFiltered: tokens.length > 0 || selectedOnly,
    totals,
    retainRow,
    clearFilters,
  };
};
